import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { notificationsApi } from "@/api/endpoints";
import type { NotificationItem } from "@/api/types";
import { formatRelativeTime } from "@/utils/format";

function notificationClass(kind: string): string {
  if (kind === "sla_breached") return "notification-item notification-sla-breach";
  if (kind === "sla_warning") return "notification-item notification-sla-warning";
  return "notification-item";
}

const SUMMARY_POLL_INTERVAL_MS = 60_000;
let summaryInFlight: Promise<number> | null = null;
let lastSummaryValue: number | null = null;
let lastSummaryFetchedAt = 0;

async function fetchSummaryUnreadCount(force = false): Promise<number> {
  const now = Date.now();
  if (!force && lastSummaryValue != null && now - lastSummaryFetchedAt < SUMMARY_POLL_INTERVAL_MS) {
    return lastSummaryValue;
  }
  if (!summaryInFlight) {
    summaryInFlight = notificationsApi
      .summary()
      .then((summary) => {
        lastSummaryValue = summary.unread_count;
        lastSummaryFetchedAt = Date.now();
        return summary.unread_count;
      })
      .finally(() => {
        summaryInFlight = null;
      });
  }
  return summaryInFlight;
}

export function NotificationCenter() {
  const navigate = useNavigate();
  const panelRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let active = true;
    function pollSummary(force = false) {
      fetchSummaryUnreadCount(force)
        .then((count) => {
          if (active) setUnreadCount(count);
        })
        .catch(() => undefined);
    }
    pollSummary(true);
    const timer = window.setInterval(pollSummary, SUMMARY_POLL_INTERVAL_MS);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    notificationsApi
      .listUnread(20)
      .then((data) => setItems(data.items))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: MouseEvent) {
      if (!panelRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  async function openNotification(item: NotificationItem) {
    await notificationsApi.markRead(item.id).catch(() => undefined);
    setUnreadCount((count) => Math.max(0, count - 1));
    if (lastSummaryValue != null) {
      lastSummaryValue = Math.max(0, lastSummaryValue - 1);
    }
    setItems((current) => current.filter((row) => row.id !== item.id));
    setOpen(false);
    if (item.claim_id) {
      navigate(`/claims/${item.claim_id}`);
    }
  }

  async function markAllRead() {
    await notificationsApi.markAllRead().catch(() => undefined);
    setUnreadCount(0);
    lastSummaryValue = 0;
    lastSummaryFetchedAt = Date.now();
    setItems([]);
  }

  return (
    <div className="notification-center" ref={panelRef}>
      <button
        type="button"
        className="btn btn-ghost notification-bell"
        aria-label="Notifications"
        onClick={() => setOpen((value) => !value)}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
          <path
            d="M12 3a5 5 0 0 0-5 5v2.1c0 .5-.2 1-.5 1.4L5.1 13.8A1 1 0 0 0 6 15.5h12a1 1 0 0 0 .9-1.5l-1.4-2.3c-.3-.4-.5-.9-.5-1.4V8a5 5 0 0 0-5-5Z"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinejoin="round"
          />
          <path d="M10 18a2 2 0 0 0 4 0" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
        {unreadCount > 0 ? <span className="notification-badge">{unreadCount > 99 ? "99+" : unreadCount}</span> : null}
      </button>
      {open ? (
        <div className="notification-dropdown card">
          <div className="notification-dropdown-header">
            <strong>Notifications</strong>
            {items.length > 0 ? (
              <button type="button" className="btn btn-ghost" onClick={markAllRead}>
                Mark all read
              </button>
            ) : null}
          </div>
          {loading ? (
            <p className="empty-state" style={{ padding: "1rem" }}>
              Loading…
            </p>
          ) : items.length === 0 ? (
            <p className="empty-state" style={{ padding: "1rem" }}>
              No unread notifications.
            </p>
          ) : (
            <ul className="notification-list">
              {items.map((item) => (
                <li key={item.id}>
                  <button type="button" className={notificationClass(item.kind)} onClick={() => openNotification(item)}>
                    <div className="notification-title">{item.title}</div>
                    <div className="notification-body">{item.body}</div>
                    <div className="notification-time">{formatRelativeTime(item.created_at)}</div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}
